import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {isPageVideo, pageObjectWithNewUids} from '@Libraries/page-object.library';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {UserVideoSource} from '@Libraries/user-video-library';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {roundToTwoDecimals} from '@Utils/math.util';
import {v4 as uuidv4} from 'uuid';

export enum PosterTypeUnits {
  PX = 'px',
  INCH = 'in',
  CM = 'cm',
  MM = 'mm',
  FT = 'ft',
}

export const doesMultipagePosterHavePagesAsTemplates = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    const templateId = pageObject.templateData?.posterId;
    if (templateId && templateId === posterObject.id) {
      return true;
    }
  }
  return false;
};

export const isPosterVideo = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (isPageVideo(pageObject)) {
      return true;
    }
  }

  return false;
};

export const doesPosterMeetGettyRequirements = (posterObject: PosterLoadObject): boolean => {
  if (!hasGettyContent(posterObject)) {
    return true;
  }

  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveNonGettyContent(pageObject)) {
      return true;
    }
  }

  return false;
};

const doesPageHaveNonGettyContent = (pageObject: PageObject): boolean => {
  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype !== ITEM_TYPE.IMAGE && item.gitype !== ITEM_TYPE.VIDEO && item.gitype !== ITEM_TYPE.SLIDESHOW) {
      return true;
    }

    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if (
        imageItem.imageSource !== ImageItemSource.GETTY &&
        imageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
        imageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      ) {
        return true;
      }
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource !== UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource !== ImageItemSource.GETTY &&
            slideImageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
            slideImageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource !== UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

const doesPageHaveGettyContent = (pageObject: PageObject): boolean => {
  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if (
        imageItem.imageSource === ImageItemSource.GETTY ||
        imageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
        imageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      ) {
        return true;
      }
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource === UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource === ImageItemSource.GETTY ||
            slideImageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
            slideImageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource === UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

export const hasGettyContent = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveGettyContent(pageObject)) {
      return true;
    }
  }
  return false;
};

export const isCustomSize = (name: string): boolean => {
  return name === 'poster-custom' || name === 'poster-custom-print' || name.includes('poster-custom-user');
};

export const getUnitsFromPosterTypeObject = (posterObject: PosterLoadObject): PosterTypeUnits => {
  if (isCustomSize(posterObject.type.name)) {
    return posterObject.units as PosterTypeUnits;
  }
  return (posterObject.type.units as PosterTypeUnits) ?? PosterTypeUnits.PX;
};

const getWidthHeightFromPosterObject = (posterObject: PosterLoadObject): {width: number; height: number} => {
  switch (getUnitsFromPosterTypeObject(posterObject)) {
    case PosterTypeUnits.MM:
      return {
        width: posterObject.type.widthMm,
        height: posterObject.type.heightMm,
      };
    case PosterTypeUnits.FT:
      return {
        width: posterObject.type.widthInch / 12,
        height: posterObject.type.heightInch / 12,
      };
    case PosterTypeUnits.INCH:
      return {
        width: posterObject.type.widthInch,
        height: posterObject.type.heightInch,
      };
    case PosterTypeUnits.CM:
      return {
        width: posterObject.type.widthCm,
        height: posterObject.type.heightCm,
      };
    case PosterTypeUnits.PX:
    default:
      return {
        width: posterObject.type.width,
        height: posterObject.type.height,
      };
  }
};

export const getPosterWidthAndHeight = (posterObject: PosterLoadObject): {width: number; height: number} => {
  if (isCustomSize(posterObject.type.name)) {
    return {
      width: Number(posterObject.userWidth),
      height: Number(posterObject.userHeight),
    };
  }
  return getWidthHeightFromPosterObject(posterObject);
};

const MM_PER_INCH = 25.4;
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

const getPixelValueForDPI = (
  width: number,
  height: number,
  units: PosterTypeUnits,
  dpi: number
): {
  width: number;
  height: number;
} => {
  let widthInInches: number;
  let heightInInches: number;
  switch (units) {
    case PosterTypeUnits.INCH:
      widthInInches = roundToTwoDecimals(width);
      heightInInches = roundToTwoDecimals(height);
      break;

    case PosterTypeUnits.MM:
      widthInInches = roundToTwoDecimals(width / MM_PER_INCH);
      heightInInches = roundToTwoDecimals(height / MM_PER_INCH);
      break;

    case PosterTypeUnits.CM:
      widthInInches = roundToTwoDecimals(width / CM_PER_INCH);
      heightInInches = roundToTwoDecimals(height / CM_PER_INCH);
      break;

    case PosterTypeUnits.FT:
      widthInInches = roundToTwoDecimals(width * INCHES_PER_FOOT);
      heightInInches = roundToTwoDecimals(height * INCHES_PER_FOOT);
      break;
    case PosterTypeUnits.PX:
    default:
      return {width: width, height: height};
  }

  return {
    width: Math.round(widthInInches * dpi),
    height: Math.round(heightInInches * dpi),
  };
};

export const getWidthAndHeightInPixels = (posterObject: PosterLoadObject): {width: number; height: number} => {
  const {width, height} = getPosterWidthAndHeight(posterObject);
  return getPixelValueForDPI(width, height, getUnitsFromPosterTypeObject(posterObject), posterObject.type.dpi);
};

export const convertPixelsToPosterUnits = (
  dpi: number,
  posterObject: PosterLoadObject,
  pixelWidth: number,
  pixelHeight: number
): {
  width: number;
  height: number;
} => {
  const units = getUnitsFromPosterTypeObject(posterObject);
  const widthInInches = pixelWidth / dpi;
  const heightInInches = pixelHeight / dpi;

  switch (units) {
    case PosterTypeUnits.INCH:
      return {
        width: Number(widthInInches.toFixed(2)),
        height: Number(heightInInches.toFixed(2)),
      };
    case PosterTypeUnits.MM:
      return {
        width: Number((widthInInches * MM_PER_INCH).toFixed(1)),
        height: Number((heightInInches * MM_PER_INCH).toFixed(1)),
      };
    case PosterTypeUnits.CM:
      return {
        width: Number((widthInInches * CM_PER_INCH).toFixed(2)),
        height: Number((heightInInches * CM_PER_INCH).toFixed(2)),
      };
    case PosterTypeUnits.FT:
      return {
        width: Number((widthInInches / INCHES_PER_FOOT).toFixed(2)),
        height: Number((heightInInches / INCHES_PER_FOOT).toFixed(2)),
      };
    case PosterTypeUnits.PX:
    default:
      return {
        width: Math.round(pixelWidth),
        height: Math.round(pixelHeight),
      };
  }
};

export const processThumbnailsFromArrayToHashmap = (pageIdsInOrder: string[], thumbnailsInOrder: string[]): Record<string, string> => {
  const hashmap: Record<string, string> = {};

  for (let i = 0; i < pageIdsInOrder.length; i++) {
    hashmap[pageIdsInOrder[i]] = thumbnailsInOrder[i];
  }

  return hashmap;
};

export const isPosterMultipage = (posterObject: PosterLoadObject): boolean => {
  return posterObject.pages.pageOrder.length > 1;
};

export const posterLoadObjectWithNewUids = (posterObject: PosterLoadObject): PosterLoadObject => {
  const newPagesHashMap: Record<string, PageObject> = {};
  const newPageOrder: string[] = [];
  const pageIdMap = new Map<string, string>();

  for (const oldPageId of posterObject.pages.pageOrder) {
    const newPageId = uuidv4();
    pageIdMap.set(oldPageId, newPageId);
    newPageOrder.push(newPageId);
  }

  for (const [oldPageId, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    const newPageId = pageIdMap.get(oldPageId);
    if (!newPageId) continue;

    const pageWithNewUids = pageObjectWithNewUids(pageObject);
    newPagesHashMap[newPageId] = {
      ...pageWithNewUids,
      hashedID: newPageId,
    };
  }

  return {
    ...posterObject,
    pages: {
      pagesHashMap: newPagesHashMap,
      pageOrder: newPageOrder,
    },
  };
};

export const isExpensiveVideoPoster = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
      if (item.gitype === ITEM_TYPE.VIDEO) {
        return true;
      }
      if (item.gitype === ITEM_TYPE.SLIDESHOW) {
        const slideshowItem = item as SlideshowItemObject;
        for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
          if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
            return true;
          }
        }
      }
    }
  }

  return false;
};
